// Match engine: deploy → player actions → world-step (bots + zone shrink) →
// win/lose. Pure & deterministic. Returns neutral SimEvents that the controller
// localizes. PROTOTYPE stand-in for the authoritative Django match service.

import { decideAction, type BotAction } from './JKGame_bots'
import { exchange } from './JKGame_combat'
import { applyBoon, rollBoonChoices } from './JKGame_boons'
import { type Loadout, PACK_COMBAT, PACK_COMPOSITION, PACK_RULES } from './JKGame_pack'
import {
  ARCHETYPES,
  FISTS,
  LOOT_ITEMS,
  LOOT_WEAPONS,
  NAME_POOL,
  PERMANENT_DANGER_ZONES,
  PLAYER_TEMPLATE,
  WEATHER_COUNT,
  ZONES,
} from './JKGame_content'
import { JKGameRng } from './JKGame_rng'
import {
  aliveEntities,
  getEntity,
  isForbidden,
  nextForbiddenZones,
  occupants,
} from './JKGame_world'
import type {
  Archetype,
  Entity,
  ExchangeResult,
  MatchState,
  RestKind,
  Weapon,
} from './JKGame_types'

export type SimEvent =
  | { k: 'move'; zone: number }
  | { k: 'searchNothing' }
  | { k: 'searchItem'; item: string }
  | { k: 'searchWeapon'; item: string }
  | { k: 'restSp'; n: number }
  | { k: 'restHp'; n: number }
  | { k: 'hazard'; dmg: number }
  | { k: 'encounter'; name: string }
  | { k: 'ambush'; name: string }
  | { k: 'playerExchange'; r: ExchangeResult } // player is attacker
  | { k: 'defendExchange'; r: ExchangeResult } // player is defender
  | { k: 'youKilled'; name: string }
  | { k: 'youDied'; name: string }
  | { k: 'youDiedEnv' }
  | { k: 'keyTaken'; name: string }
  | { k: 'targetGone' }
  | { k: 'fled' }
  | { k: 'special' }
  | { k: 'botKill'; killer: string; victim: string }
  | { k: 'collar'; name: string; isPlayer: boolean }
  | { k: 'keyLost'; name: string } // keyholder collar-died → key re-issued to a survivor
  | { k: 'broadcast'; zones: number[] }
  | { k: 'boonOffer'; choices: string[] } // player kill → three-choice run buff offered
  | { k: 'boonTaken'; id: string } // player picked a boon

export type FailKey = 'forbidden' | 'sameZone' | 'noSp' | 'deadAct' | 'noKey' | 'notRunning' | 'noRage' | 'noBoon'

/** Rage needed to unleash a special (必殺技). From content/packs/combat.json. */
export const RAGE_SPECIAL = PACK_COMBAT.rageSpecial

export interface ActionOutcome {
  ok: boolean
  failKey?: FailKey
  events: SimEvent[]
}

const PLAYER_ID = 'P'
// Rule constants from content/packs/rules.json (mirrored on the backend).
// Move costs are exported so presentation (ExploreScene pre-check) shares one source.
export const MOVE_COST = PACK_RULES.moveCost
export const MOVE_COST_HURT = PACK_RULES.moveCostHurt
const SEARCH_COST = 12
const AREA_INTERVAL = PACK_RULES.areaInterval
const AREA_ADD = PACK_RULES.areaAdd
const BOT_COUNT_DEFAULT = PACK_RULES.botCount // player + 41 = 42 「同班同學」(novel class size)
// Hard safety bound on roster size, mirroring the backend sim's MAX_BOTS (1:1 parity). The
// game only ever asks for BOT_COUNT_DEFAULT, but createMatch is shared/exported — guards a
// caller passing a huge count (push-loop DoS) or a NEGATIVE one (which would set
// roster.length negative → RangeError crash). Floor 0 = a valid instant-survivor match.
const MAX_BOTS = 200

// Archetype composition for the bot roster (sums to BOT_COUNT_DEFAULT at 41).
// Loaded + zod-validated from content/packs/composition.json (single source of
// truth, mirrored on the backend). Order == roster build order; do not reorder.
const COMPOSITION = PACK_COMPOSITION

function cloneWeapon(w: Weapon): Weapon {
  return { ...w }
}

function rstat(v: number | [number, number], rng: JKGameRng): number {
  return Array.isArray(v) ? rng.int(v[0], v[1]) : v
}

function weaponScore(w: Weapon): number {
  return w.kind === 'N' ? 0 : w.kind === 'P' || w.kind === 'K' ? w.power * 2 : w.power
}

function makeStartWeapon(spec: 'fists' | 'random' | Weapon, rng: JKGameRng): Weapon {
  if (spec === 'fists') return cloneWeapon(FISTS)
  if (spec === 'random') return cloneWeapon(rng.pick(LOOT_WEAPONS))
  return cloneWeapon(spec)
}

function buildBot(id: string, name: string, archetype: Archetype, zone: number, rng: JKGameRng): Entity {
  const tpl = ARCHETYPES[archetype as Exclude<Archetype, 'player'>]
  const mhp = rstat(tpl.mhp, rng)
  const msp = rstat(tpl.msp, rng)
  return {
    id,
    name,
    archetype,
    isPlayer: false,
    gender: rng.chance(0.5) ? 'm' : 'f',
    icon: rng.int(1, 42),
    zone,
    hp: mhp,
    mhp,
    sp: msp,
    msp,
    att: rstat(tpl.att, rng),
    def: rstat(tpl.def, rng),
    armor: tpl.armor,
    lvl: 1,
    skill: rstat(tpl.skill, rng),
    rage: 0,
    weapon: makeStartWeapon(tpl.startWeapon, rng),
    injuries: new Set(),
    items: [],
    hasKey: archetype === 'boss',
    alive: true,
    kills: 0,
    deathStep: -1,
    passive: 'none', // bots carry no loadout passive (only the player does)
  }
}

// `loadout` (出戰角色 roguelite loadout) overrides ONLY the player's start weapon + passive;
// base stats stay on PLAYER_TEMPLATE so PvE balance is unchanged (side-grade, not power-creep).
// Applied RNG-FREE (no rng.* calls) → same (seed, loadout) always rebuilds the identical match.
// `ranked` = fair mode: ignore the loadout + suppress boons (mirrors the backend create_match), so
// a paid/unlocked character grants no edge on the ranked board (docs/playability-monetization.md §6).
export function createMatch(seed: number, botCount: number = BOT_COUNT_DEFAULT, loadout?: Loadout | null, ranked = false): MatchState {
  // Clamp to [0, MAX_BOTS] (parity with the backend create_match): a negative count would
  // crash at `roster.length = …` below (RangeError), a huge one would DoS the push loop.
  botCount = Math.max(0, Math.min(Math.floor(botCount), MAX_BOTS))
  const rng = new JKGameRng(seed)
  // Playable zones exclude the permanent danger zone (School); only these get
  // spawned into and progressively forbidden by the shrinking circle.
  const playable = Array.from({ length: ZONES.length }, (_v, i) => i).filter((i) => !PERMANENT_DANGER_ZONES.has(i))
  const forbiddenOrder = rng.shuffle([...playable])

  // PARITY NOTE: this picks the player's zone (rng.pick below) BEFORE shuffling the roster,
  // but the backend (source of truth) shuffles the roster FIRST, then picks the player zone
  // (create_match → _player). So the RNG stream forks here and the same seed builds a different
  // initial match on the two sims (player zone, roster, bot stats, weather all desync from step
  // 0). Fix (move the roster build+shuffle above this player object) is flagged as a deliberate
  // flag-off behaviour change pending sign-off; left as-is this turn to stay no-op.
  const ld = ranked ? null : (loadout ?? null) // ranked ignores the loadout → everyone on base stats
  const player: Entity = {
    id: PLAYER_ID,
    name: '你',
    archetype: 'player',
    isPlayer: true,
    gender: 'm',
    icon: 1,
    zone: rng.pick(playable),
    hp: PLAYER_TEMPLATE.mhp,
    mhp: PLAYER_TEMPLATE.mhp,
    sp: PLAYER_TEMPLATE.msp,
    msp: PLAYER_TEMPLATE.msp,
    att: PLAYER_TEMPLATE.att,
    def: PLAYER_TEMPLATE.def,
    armor: PLAYER_TEMPLATE.armor,
    lvl: 1,
    skill: PLAYER_TEMPLATE.skill,
    rage: 0,
    weapon: cloneWeapon(ld?.weapon ?? PLAYER_TEMPLATE.weapon),
    injuries: new Set(),
    items: [],
    hasKey: false,
    alive: true,
    kills: 0,
    deathStep: -1,
    passive: ld?.passive ?? 'none',
  }

  const roster: Archetype[] = []
  for (const [arch, n] of COMPOSITION) for (let i = 0; i < n; i++) roster.push(arch)
  // pad/trim to requested botCount
  while (roster.length < botCount) roster.push('survivor')
  roster.length = Math.min(roster.length, botCount)
  rng.shuffle(roster)

  const entities: Entity[] = [player]
  roster.forEach((arch, i) => {
    const base = NAME_POOL[i % NAME_POOL.length]
    const name = `${base}${i + 1}` // 座號-style suffix → unique + flavorful
    entities.push(buildBot(`B${i}`, name, arch, rng.pick(playable), rng))
  })

  return {
    seed,
    step: 0,
    weather: rng.int(0, WEATHER_COUNT - 1),
    forbiddenOrder,
    forbiddenCount: 0,
    areaInterval: AREA_INTERVAL,
    areaAdd: AREA_ADD,
    stepsToShrink: AREA_INTERVAL,
    entities,
    playerId: PLAYER_ID,
    pendingTargetId: null,
    log: [],
    status: 'running',
    winnerId: null,
    winReason: null,
    boons: [],
    pendingBoon: null,
    ranked,
  }
}

// ─── helpers ───────────────────────────────────────────────────────────────

function player(match: MatchState): Entity {
  // PLAYER_ID always exists for the lifetime of a match.
  return getEntity(match, PLAYER_ID) as Entity
}

function stampDeaths(match: MatchState): void {
  for (const e of match.entities) if (!e.alive && e.deathStep < 0) e.deathStep = match.step
}

// Pick the living entity that inherits a collar-stranded escape key (see worldStep). Mirrors
// the backend _key_heir 1:1: deterministic and RNG-free (so it can't compound the createMatch
// RNG fork) — the survivor with the most kills, ties broken by roster order. null only when
// nobody is alive, leaving the key on the corpse (the match is already resolving).
function keyHeir(match: MatchState): Entity | null {
  let best: Entity | null = null
  for (const e of aliveEntities(match)) if (best === null || e.kills > best.kills) best = e
  return best
}

function guard(match: MatchState): FailKey | null {
  if (match.status !== 'running') return 'notRunning'
  if (!player(match).alive) return 'deadAct'
  return null
}

// Resolve what happens when the player ends up sharing a zone with enemies.
function resolveEncounter(match: MatchState, rng: JKGameRng): SimEvent[] {
  if (match.pendingTargetId) return []
  const p = player(match)
  const hostiles = occupants(match, p.zone, PLAYER_ID)
  if (hostiles.length === 0) return []

  const target = hostiles[rng.int(0, hostiles.length - 1)]
  if (rng.pct(55)) {
    match.pendingTargetId = target.id
    return [{ k: 'encounter', name: target.name }]
  }

  // ambush: enemy strikes first; player auto-counters inside exchange()
  const r = exchange(target, p, rng)
  const evs: SimEvent[] = [{ k: 'ambush', name: target.name }, { k: 'defendExchange', r }]
  if (r.defenderDied) evs.push({ k: 'youDied', name: target.name })
  else if (r.attackerDied) {
    evs.push({ k: 'youKilled', name: target.name })
    if (r.keyTaken) evs.push({ k: 'keyTaken', name: target.name })
  } else match.pendingTargetId = target.id
  return evs
}

// ─── player actions ──────────────────────────────────────────────────────────

// Maze navigation: enter an adjacent room. Spatial encounters are handled by
// the Explore scene (you walk into a bot), so this no longer rolls a random
// encounter — it just relocates and pays stamina.
export function playerMove(match: MatchState, zone: number, _rng: JKGameRng): ActionOutcome {
  const fail = guard(match)
  if (fail) return { ok: false, failKey: fail, events: [] }
  const p = player(match)
  // Mirror the backend anti-cheat guard (1:1 sim parity): reject an out-of-range
  // zone — it would slip past isForbidden (which only matches real zones). Legit
  // play only ever supplies neighbour zones, so this never fires in normal use.
  if (zone < 0 || zone >= ZONES.length) return { ok: false, failKey: 'forbidden', events: [] }
  if (isForbidden(match, zone)) return { ok: false, failKey: 'forbidden', events: [] }
  if (zone === p.zone) return { ok: false, failKey: 'sameZone', events: [] }
  const cost = p.injuries.has('f') ? MOVE_COST_HURT : MOVE_COST
  if (p.sp <= cost) return { ok: false, failKey: 'noSp', events: [] }

  p.sp -= cost
  p.zone = zone
  match.pendingTargetId = null
  stampDeaths(match)
  return { ok: true, events: [{ k: 'move', zone }] }
}

/** Player walked into a specific bot → enter combat with it (no world tick). */
export function beginCombat(match: MatchState, targetId: string): boolean {
  const p = player(match)
  if (match.status !== 'running' || !p.alive) return false
  const target = getEntity(match, targetId)
  // `target === p` guards self-targeting (a malicious 'P' id) — never engage self.
  if (!target || target === p || !target.alive || target.zone !== p.zone) return false
  match.pendingTargetId = targetId
  return true
}

export function pickupWeapon(match: MatchState, w: Weapon): void {
  const p = player(match)
  if (p.alive) p.weapon = { ...w }
}

export function pickupItem(match: MatchState, item: string): void {
  const p = player(match)
  if (p.alive) p.items.push(item)
}

export function playerSearch(match: MatchState, rng: JKGameRng): ActionOutcome {
  const fail = guard(match)
  if (fail) return { ok: false, failKey: fail, events: [] }
  const p = player(match)
  const cost = p.injuries.has('a') ? SEARCH_COST + 6 : SEARCH_COST
  if (p.sp <= cost) return { ok: false, failKey: 'noSp', events: [] }
  p.sp -= cost

  const events: SimEvent[] = []
  if (rng.pct(18)) {
    const dmg = rng.int(3, 8)
    p.hp -= dmg
    events.push({ k: 'hazard', dmg })
    if (p.hp <= 0) {
      p.hp = 0
      p.alive = false
      events.push({ k: 'youDiedEnv' })
    }
    stampDeaths(match)
    return { ok: true, events }
  }

  const hostiles = occupants(match, p.zone, PLAYER_ID)
  if (hostiles.length > 0 && rng.pct(60)) {
    events.push(...resolveEncounter(match, rng))
  } else if (rng.pct(50)) {
    const w = rng.pick(LOOT_WEAPONS)
    if (weaponScore(w) > weaponScore(p.weapon)) {
      p.weapon = cloneWeapon(w)
      events.push({ k: 'searchWeapon', item: w.name })
    } else {
      const item = rng.pick(LOOT_ITEMS)
      p.items.push(item)
      events.push({ k: 'searchItem', item })
    }
  } else {
    events.push({ k: 'searchNothing' })
  }
  stampDeaths(match)
  return { ok: true, events }
}

export function playerRest(match: MatchState, kind: RestKind, rng: JKGameRng): ActionOutcome {
  const fail = guard(match)
  if (fail) return { ok: false, failKey: fail, events: [] }
  const p = player(match)
  if (kind === 'sleep') {
    const old = p.sp
    p.sp = Math.min(p.msp, p.sp + rng.int(20, 35))
    return { ok: true, events: [{ k: 'restSp', n: p.sp - old }] }
  }
  const old = p.hp
  p.hp = Math.min(p.mhp, p.hp + rng.int(12, 22))
  return { ok: true, events: [{ k: 'restHp', n: p.hp - old }] }
}

// After a player kill, offer a three-choice run buff — but only while enemies remain (no point
// offering it on the kill that wins the match). Choices are deterministic from (seed, kills),
// so the offer is reroll-proof and replayable. Uses an independent RNG stream (see rollBoonChoices).
function offerBoonAfterKill(match: MatchState, events: SimEvent[]): void {
  if (match.ranked) return // ranked = fair: no boons (parity with the backend)
  const p = player(match)
  const enemiesLeft = match.entities.some((e) => e !== p && e.alive)
  if (!enemiesLeft) return
  const choices = rollBoonChoices(match.seed, p.kills)
  match.pendingBoon = choices
  events.push({ k: 'boonOffer', choices })
}

// Apply a player-chosen run buff. A free meta-action (no world-step) — the controller routes it
// without ticking. Validates the id is one of the pending offer (can't inject an arbitrary boon).
export function applyChosenBoon(match: MatchState, boonId: string): ActionOutcome {
  if (match.status !== 'running') return { ok: false, failKey: 'notRunning', events: [] }
  if (!match.pendingBoon || !match.pendingBoon.includes(boonId)) {
    return { ok: false, failKey: 'noBoon', events: [] }
  }
  applyBoon(player(match), boonId)
  match.boons.push(boonId)
  match.pendingBoon = null
  return { ok: true, events: [{ k: 'boonTaken', id: boonId }] }
}

export function playerAttack(match: MatchState, targetId: string, rng: JKGameRng): ActionOutcome {
  const fail = guard(match)
  if (fail) return { ok: false, failKey: fail, events: [] }
  const p = player(match)
  const target = getEntity(match, targetId)
  if (!target || target === p || !target.alive || target.zone !== p.zone) {
    match.pendingTargetId = null
    return { ok: true, events: [{ k: 'targetGone' }] }
  }

  const r = exchange(p, target, rng)
  const events: SimEvent[] = [{ k: 'playerExchange', r }]
  if (r.defenderDied) {
    events.push({ k: 'youKilled', name: target.name })
    if (r.keyTaken) events.push({ k: 'keyTaken', name: target.name })
    match.pendingTargetId = null
    offerBoonAfterKill(match, events)
  } else if (r.attackerDied) {
    events.push({ k: 'youDied', name: target.name })
    match.pendingTargetId = null
  } else {
    match.pendingTargetId = target.id
  }
  stampDeaths(match)
  return { ok: true, events }
}

/** Special move: spends rage for an empowered (×2, guaranteed-crit) strike. */
export function playerSpecial(match: MatchState, targetId: string, rng: JKGameRng): ActionOutcome {
  const fail = guard(match)
  if (fail) return { ok: false, failKey: fail, events: [] }
  const p = player(match)
  if (p.rage < RAGE_SPECIAL) return { ok: false, failKey: 'noRage', events: [] }
  const target = getEntity(match, targetId)
  if (!target || target === p || !target.alive || target.zone !== p.zone) {
    match.pendingTargetId = null
    return { ok: true, events: [{ k: 'targetGone' }] }   // self-target guarded before any rage spend
  }
  p.rage -= RAGE_SPECIAL
  const r = exchange(p, target, rng, 2.0)
  const events: SimEvent[] = [{ k: 'special' }, { k: 'playerExchange', r }]
  if (r.defenderDied) {
    events.push({ k: 'youKilled', name: target.name })
    if (r.keyTaken) events.push({ k: 'keyTaken', name: target.name })
    match.pendingTargetId = null
    offerBoonAfterKill(match, events)
  } else if (r.attackerDied) {
    events.push({ k: 'youDied', name: target.name })
    match.pendingTargetId = null
  } else {
    match.pendingTargetId = target.id
  }
  stampDeaths(match)
  return { ok: true, events }
}

export function playerFlee(match: MatchState, rng: JKGameRng): ActionOutcome {
  const fail = guard(match)
  if (fail) return { ok: false, failKey: fail, events: [] }
  const p = player(match)
  p.sp = Math.max(0, p.sp - rng.int(5, 10))
  match.pendingTargetId = null
  return { ok: true, events: [{ k: 'fled' }] }
}

export function playerEscape(match: MatchState): ActionOutcome {
  const fail = guard(match)
  if (fail) return { ok: false, failKey: fail, events: [] }
  if (!player(match).hasKey) return { ok: false, failKey: 'noKey', events: [] }
  match.status = 'over'
  match.winnerId = PLAYER_ID
  match.winReason = 'escape'
  return { ok: true, events: [] }
}

// ─── world step (bots + zone shrink) ─────────────────────────────────────────

function applyBot(self: Entity, action: BotAction, match: MatchState, rng: JKGameRng, evs: SimEvent[]): void {
  switch (action.type) {
    case 'move':
      if (!isForbidden(match, action.zone)) {
        self.zone = action.zone
        self.sp = Math.max(0, self.sp - 10)
      }
      return
    case 'search':
      if (self.weapon.kind === 'N' && rng.pct(40)) self.weapon = cloneWeapon(rng.pick(LOOT_WEAPONS))
      else if (rng.pct(30)) self.hp = Math.min(self.mhp, self.hp + rng.int(6, 12))
      self.sp = Math.max(0, self.sp - 8)
      return
    case 'rest':
      if (action.kind === 'sleep') self.sp = Math.min(self.msp, self.sp + rng.int(15, 30))
      else self.hp = Math.min(self.mhp, self.hp + rng.int(10, 18))
      return
    case 'attack': {
      const target = getEntity(match, action.targetId)
      if (!target || !target.alive || target.zone !== self.zone) return
      const r = exchange(self, target, rng)
      if (target.isPlayer) {
        evs.push({ k: 'defendExchange', r })
        if (r.defenderDied) evs.push({ k: 'youDied', name: self.name })
        else if (r.attackerDied) {
          evs.push({ k: 'youKilled', name: self.name })
          if (r.keyTaken) evs.push({ k: 'keyTaken', name: self.name })
        } else match.pendingTargetId = self.id
      } else {
        if (r.defenderDied) evs.push({ k: 'botKill', killer: self.name, victim: target.name })
        else if (r.attackerDied) evs.push({ k: 'botKill', killer: target.name, victim: self.name })
      }
      return
    }
    case 'idle':
      return
  }
}

export function worldStep(match: MatchState, rng: JKGameRng): SimEvent[] {
  if (match.status !== 'running') return []
  const evs: SimEvent[] = []
  match.step++

  // 1) Bots act (snapshot ids; skip any that died mid-step).
  const botIds = match.entities.filter((e) => !e.isPlayer && e.alive).map((e) => e.id)
  for (const id of botIds) {
    const self = getEntity(match, id)
    if (!self || !self.alive) continue
    applyBot(self, decideAction(self, match, rng), match, rng, evs)
  }

  // 2) Zone shrink + collar deaths for anyone who failed to flee.
  match.stepsToShrink--
  if (match.stepsToShrink <= 0) {
    match.forbiddenCount = Math.min(match.forbiddenOrder.length - 1, match.forbiddenCount + AREA_ADD)
    let strandedHolder: Entity | null = null
    for (const e of match.entities) {
      if (e.alive && isForbidden(match, e.zone)) {
        e.hp = 0
        e.alive = false
        evs.push({ k: 'collar', name: e.name, isPlayer: e.isPlayer })
        if (e.hasKey) strandedHolder = e // only ever one key → at most one stranded holder per shrink
      }
    }
    // A collar death credits no killer (applyDeath never runs), so the escape key would otherwise
    // STRAND on the corpse → 0 living keyholders → the escape win-path silently vanishes for the
    // match. Re-issue it to a survivor (keyHeir) so the featured key-hunt stays alive. After the
    // collar loop so the heir is a true survivor, never someone collared the same step. (1:1 with
    // the authoritative backend world_step.)
    if (strandedHolder) {
      const heir = keyHeir(match)
      if (heir) {
        strandedHolder.hasKey = false
        heir.hasKey = true
        evs.push({ k: 'keyLost', name: strandedHolder.name })
      }
    }
    match.stepsToShrink = AREA_INTERVAL
    const upcoming = nextForbiddenZones(match)
    if (upcoming.length > 0) evs.push({ k: 'broadcast', zones: upcoming })
  }

  stampDeaths(match)
  return evs
}

// ─── win / lose ──────────────────────────────────────────────────────────────

export function checkGameOver(match: MatchState): void {
  if (match.status !== 'running') return
  const p = player(match)
  if (!p.alive) {
    match.status = 'over'
    match.winnerId = null
    match.winReason = 'dead'
    return
  }
  const alive = aliveEntities(match)
  if (alive.length === 1 && alive[0].id === PLAYER_ID) {
    match.status = 'over'
    match.winnerId = PLAYER_ID
    match.winReason = 'survivor'
  }
}

export function computeRank(match: MatchState): { rank: number; total: number } {
  const total = match.entities.length
  const p = player(match)
  if (p.alive) return { rank: 1, total }
  const outlived = match.entities.filter(
    (e) => e.id !== PLAYER_ID && (e.alive || e.deathStep > p.deathStep),
  ).length
  return { rank: outlived + 1, total }
}
